/**
 * What every part of the New request sheet can reach — `docs/new-request.md`
 * §5–§12, §16.
 *
 * The orchestrator (`new-request-sheet.tsx`) owns the state and hands this to
 * the four sections and the schedule aside through one context, so a section
 * reads the facts it needs (the property's coordinates, the division, the
 * length) without the orchestrator naming each one, and the aside and the
 * appointment section — two ends of one choice — write to the same place.
 *
 * Nothing here computes a distance, a drive or a grade (rule 2): the server
 * answers, and `placementOf` (a `shared` function the server also runs) is the
 * one rule the sheet applies to a block somebody dragged.
 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { fromZonedTime } from 'date-fns-tz'
import type { DayPlan, Opening, Segment } from '@rowted/shared'
import { requestsApi, type Recommendation, type RequestSetup } from './api.js'
import type { Missing, SectionKey, SheetState } from './model.js'

/** A person the sheet can show a calendar for or choose. */
export interface PersonRef {
  id: string
  name: string
  firstName: string
}

/** The facts the sections and the aside read, worked out once from the state. */
export interface RequestFacts {
  contactId: string | null
  contactFirstName: string | null
  contactName: string | null
  /** The property's coordinates, once chosen or confirmed. */
  place: { lat: number; lng: number } | null
  /** The property's branch: chosen, the check's answer, or what the person picked. */
  orgUnitId: string | null
  branchName: string | null
  divisionCode: string | null
  divisionName: string | null
  /** The division's estimate visit, for Length's default and its hint. */
  divisionMinutes: number | null
  segment: Segment
  /** The visit's length: what was picked, else the division's estimate, else 60. */
  minutes: number
  /** The property's name for the new block and the When box (*Castillo Residence*). */
  propertyName: string | null
  /** Its street line, for the new block (*412 Orchard Ridge Dr*). */
  street: string | null
  /** The job's title, for the new block (*front walk and patio*). */
  jobTitle: string | null
}

/** Which calendars the aside is showing (§10.1). */
export interface ScheduleState {
  mode: 'one' | 'side'
  /** One: the person shown is `people[0]`. Side by side: up to three. */
  people: PersonRef[]
  /** The day under the strip, or null for the person's best day. */
  day: string | null
  /** The strip's first day, or null for today (*Tue Nov 3 ›* moves it). */
  first: string | null
}

/** Book mode (§11.4): a job that already exists, given a time. */
export interface BookContext {
  jobId: string
  reference: string
  appointmentId: string | null
  propertyName: string
}

/** A time somebody chose, from a card, Best times, a gap or a drag. */
export interface ChosenTime {
  date: string
  start: number
  startsAt: string
}

export interface RequestSheetApi {
  requestId: string
  setup: RequestSetup
  /** A phone: full screen, bottom sheets, no footer (§16.3). */
  phone: boolean
  /** Under 1,180 px: opening a calendar replaces the form (§16.2). */
  narrow: boolean
  state: SheetState
  update: (change: (state: SheetState) => SheetState) => void
  facts: RequestFacts
  book: BookContext | null

  /**
   * §5.4, from anywhere: the search, *Your properties* in the address box, a
   * radio row under *who at …*, a card under a contact. The sheet loads the
   * record, fills both sections as §5.4 says, opens the next one, and asks
   * *Replace what's in …?* first when the section holds typed values (§19 B2).
   */
  chooseContact: (contactId: string, options?: { propertyId?: string; fromProperty?: boolean }) => void
  /**
   * `inSection`: chosen from inside the Property section itself (*Your
   * properties*, *Use <name>*), so what was typed there is being replaced on
   * purpose and §19 B2's question is not asked.
   */
  chooseProperty: (
    propertyId: string, options?: { contactId?: string; fromContact?: boolean; inSection?: boolean },
  ) => void
  /** §19 B2's question, drawn under the section's heading while it waits. */
  replaceAsk: { section: 'contact' | 'property'; name: string } | null
  answerReplace: (replace: boolean) => void
  /** **Change**: the section back to empty, focus in the search (§5.4). */
  clearSection: (section: 'contact' | 'property') => void
  /**
   * Put the caret in a field: *A new property on …* focuses the address box
   * and opens Google's suggestions; Create with something missing focuses the
   * first. `n` changes every time, so the same field can be asked for twice.
   */
  focus: { field: string; n: number } | null
  requestFocus: (field: string) => void

  /** The section in view, for the bar's tinted chip. */
  current: SectionKey | null
  /** Open a section where it is and scroll to it (a waiting row, a chip, Open, Edit). */
  openSection: (section: SectionKey) => void

  /** After Create was pressed with something missing: the red line under a field. */
  hintFor: (field: Missing['field']) => string | null
  missing: Missing[] | null

  /** Who goes: the server's recommendation for this request, or null until it can be asked. */
  recommendation: Recommendation | null
  recommendationLoading: boolean

  /** The aside: null while closed. */
  schedule: ScheduleState | null
  setSchedule: (change: (schedule: ScheduleState | null) => ScheduleState | null) => void
  /** *See calendar* (One, that person); `choose` also makes them Who goes. */
  openCalendar: (person: PersonRef, options?: { choose?: boolean }) => void
  /** *Compare calendars* (Side by side, the recommended people, at most three). */
  compareCalendars: (people: PersonRef[]) => void
  closeSchedule: () => void

  /**
   * Who goes, and when: a card's radio (no time), its time button, a Best-time
   * card, a green gap, a drag. A null time keeps the person and clears it.
   */
  choose: (person: PersonRef, time: ChosenTime | null) => void
  /** Move the chosen time (the When box's *Move to*, a drag, typing Starts). */
  moveTo: (time: ChosenTime) => void

  /** The chosen person's day, for the When box's OK · Tight · Late (§10.8). */
  chosenPlan: DayPlan | null
  /** The aside tells the sheet a day it fetched, so the When box needs no second call. */
  rememberPlan: (personId: string, plan: DayPlan) => void
  /** *Book it anyway* was pressed for this exact time (§10.8, once). */
  bookAnyway: string | null
  setBookAnyway: (key: string | null) => void
}

const SheetContext = createContext<RequestSheetApi | null>(null)

export const RequestSheetProvider = SheetContext.Provider

/** The sheet's API where a part may also be drawn outside it (a test, a preview). */
export function useRequestSheetMaybe(): RequestSheetApi | null {
  return useContext(SheetContext)
}

export function useRequestSheet(): RequestSheetApi {
  const api = useContext(SheetContext)
  if (!api) throw new Error('useRequestSheet outside the New request sheet')
  return api
}

/** The instant a day and minute in a zone are — a drag's `startsAt`. */
export function instantOf(date: string, minutes: number, timeZone: string): string {
  const hh = String(Math.floor(minutes / 60)).padStart(2, '0')
  const mm = String(minutes % 60).padStart(2, '0')
  return fromZonedTime(`${date}T${hh}:${mm}:00`, timeZone).toISOString()
}

/** An opening as the time it chooses. */
export const timeOf = (opening: Opening): ChosenTime =>
  ({ date: opening.date, start: opening.start, startsAt: opening.startsAt })

/**
 * The server's recommendation for this request (§9.2), asked again when the
 * branch, the place, the division, the segment or the length changes — and
 * not at all until the place and the division are known (§9.1).
 */
export function useRecommendation(requestId: string, facts: RequestFacts): {
  recommendation: Recommendation | null
  loading: boolean
} {
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null)
  const [loading, setLoading] = useState(false)
  const key = facts.place && facts.orgUnitId && facts.divisionCode
    ? [facts.orgUnitId, facts.divisionCode, facts.segment, facts.place.lat, facts.place.lng,
      facts.minutes, facts.contactId ?? ''].join('|')
    : null

  useEffect(() => {
    if (!key || !facts.place || !facts.orgUnitId || !facts.divisionCode) {
      setRecommendation(null)
      return
    }
    let live = true
    setLoading(true)
    requestsApi.salespeople({
      session: requestId, orgUnitId: facts.orgUnitId, division: facts.divisionCode,
      segment: facts.segment, lat: facts.place.lat, lng: facts.place.lng, minutes: facts.minutes,
      contactId: facts.contactId,
    })
      .then((answer) => { if (live) setRecommendation(answer) })
      .catch(() => { if (live) setRecommendation(null) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
    /* The key is the whole question; the facts it was made from ride along. */
  }, [key, requestId])

  return useMemo(() => ({ recommendation, loading }), [recommendation, loading])
}
